import { useState, useMemo, lazy, Suspense, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const AddSubscriptionDialog = lazy(() =>
  import("./AddSubscriptionDialog").then((module) => ({
    default: module.AddSubscriptionDialog,
  })),
);
const SubscriptionListFilters = lazy(() =>
  import("./SubscriptionListFilters").then((module) => ({
    default: module.SubscriptionListFilters,
  })),
);

import { SubscriptionListContent } from "./subscription-list/SubscriptionListContent";
import { useCategories } from "@/hooks/useCategories";
import { useLifecycleEvents } from "@/hooks/useLifecycleEvents";
import { usePaymentHistory } from "@/hooks/usePaymentHistory";
import { useSubscriptionFiltering } from "@/hooks/useSubscriptionFiltering";
import { useSubscriptionPeriods } from "@/hooks/useSubscriptionPeriods";
import { useSubscriptions } from "@/hooks/useSubscriptions";
import { useSubscriptionTags } from "@/hooks/useSubscriptionTags";
import { transformFromDatabase } from "@/lib/dataTransformers";
import { type ViewingPeriod } from "@/lib/periodStorageUtils";
import { formatPeriodDisplay } from "@/lib/subscriptionDateUtils";

interface SubscriptionListProps {
  currentPeriod: ViewingPeriod;
  onPeriodChange: (period: ViewingPeriod) => void;
  onNavigatePeriod: (direction: "prev" | "next") => void;
  onSetToCurrentMonth: () => void;
  onSetToNextMonth: () => void;
}

export const SubscriptionList = ({
  currentPeriod,
  onPeriodChange,
  onNavigatePeriod,
  onSetToCurrentMonth,
  onSetToNextMonth,
}: SubscriptionListProps) => {
  const { subscriptions: dbSubscriptions, isLoading } = useSubscriptions();
  const { categories: dbCategories } = useCategories();
  const { payments } = usePaymentHistory();
  const { lifecycleEvents } = useLifecycleEvents();
  const { subscriptionPeriods } = useSubscriptionPeriods();
  const { tagsBySubscriptionId } = useSubscriptionTags();

  const [editingSubscription, setEditingSubscription] = useState<string | null>(
    null,
  );
  const [searchParams, setSearchParams] = useSearchParams();

  // Transform database subscriptions to app format
  const subscriptions = useMemo(() => {
    if (!dbSubscriptions.length) return [];

    return dbSubscriptions.map((dbSub) =>
      transformFromDatabase(
        dbSub,
        dbCategories,
        payments,
        lifecycleEvents,
        subscriptionPeriods,
      ),
    );
  }, [
    dbSubscriptions,
    dbCategories,
    payments,
    lifecycleEvents,
    subscriptionPeriods,
  ]);

  // Handle subscription query parameter from notifications
  useEffect(() => {
    const subscriptionId = searchParams.get("subscription");
    if (subscriptionId && subscriptions.length > 0) {
      // Check if subscription exists
      const subscription = subscriptions.find(
        (sub) => sub.id === subscriptionId,
      );
      if (subscription) {
        setEditingSubscription(subscriptionId);
        // Remove the query parameter after opening the dialog
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, subscriptions, setSearchParams]);

  // Custom hook for filtering and sorting subscriptions
  const filteringState = useSubscriptionFiltering({
    subscriptions,
    currentPeriod,
    payments,
    tagsBySubscriptionId,
    categories: dbCategories,
  });

  const getEditingSubscription = () => {
    if (!editingSubscription) return undefined;

    const found = subscriptions.find((sub) => sub.id === editingSubscription);
    if (!found) {
      console.warn(`Subscription with ID ${editingSubscription} not found`);
      setEditingSubscription(null); // Clear invalid state
    }
    return found;
  };

  const getCurrentPeriodDisplay = () =>
    formatPeriodDisplay(currentPeriod.month, currentPeriod.year);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="py-8 text-center">
          <p className="text-muted-foreground">Loading subscriptions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <Suspense
        fallback={
          <div className="flex h-24 items-center justify-center text-muted-foreground">
            Loading filters...
          </div>
        }
      >
        <SubscriptionListFilters
          searchTerm={filteringState.searchTerm}
          onSearchChange={filteringState.setSearchTerm}
          filterStatus={filteringState.filterStatus}
          onStatusChange={filteringState.setFilterStatus}
          tagFilters={filteringState.filterTags}
          onTagFiltersChange={filteringState.setFilterTags}
          sortBy={filteringState.sortBy}
          onSortChange={(value) => filteringState.setSortBy(value as any)}
          categories={filteringState.uniqueCategories}
        />
      </Suspense>

      {/* Subscription Cards */}
      <SubscriptionListContent
        filteredSubscriptions={filteringState.filteredSubscriptions}
        totalSubscriptions={subscriptions.length}
        subscriptionsInPeriod={filteringState.subscriptionsInPeriod.length}
        currentPeriod={currentPeriod}
        periodDisplay={getCurrentPeriodDisplay()}
        onEdit={setEditingSubscription}
      />

      {/* Edit Dialog */}
      {editingSubscription && (
        <Suspense fallback={<div />}>
          <AddSubscriptionDialog
            subscription={getEditingSubscription()}
            onComplete={() => setEditingSubscription(null)}
          />
        </Suspense>
      )}

      {/* Floating Add Button */}
      <Suspense fallback={<div />}>
        <AddSubscriptionDialog />
      </Suspense>
    </div>
  );
};
